import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { BookOpenIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { criteriaAPI } from '../services/standardsApi';
import { getApiErrorMessage } from '../utils/apiError';

// New page, not a copy: section 6's CriteriaPage belongs to the video-review phase (P6/P7,
// "table + detail panel", done-when "all 30 rows list and 'View' opens the panel with
// standard text" - section 6.9 item 11) and the main app has no CriteriaPage.js to mirror.
// This phase (P4) needs the /criteria route to resolve today, so this is a minimal table +
// panel over the read-only GET /criteria / GET /criteria/{id} contract (18.2, section 7's
// CriterionListResponse/CriterionDetailResponse shapes) that already satisfies that
// done-when check; a later phase can extend it if the review UI needs more from it.
export default function CriteriaPage() {
  const [selectedId, setSelectedId] = useState(null);

  const { data, isLoading, error } = useQuery(
    ['criteria'],
    () => criteriaAPI.getAll(),
    { select: (res) => res.data }
  );

  const { data: detail, isLoading: detailLoading, error: detailError } = useQuery(
    ['criterion', selectedId],
    () => criteriaAPI.getById(selectedId),
    { select: (res) => res.data, enabled: Boolean(selectedId) }
  );

  const criteria = data?.criteria || [];

  // "All 30 Table 6 criteria" used to be typed in by hand - true only because the seed
  // happens to load exactly 30 rows today, and silently wrong the moment that seed changes
  // without anyone remembering to edit this string. criteria.length is the same array the
  // table below renders, so the count in the subtitle can never disagree with the count of
  // rows a user can actually see and count themselves. Loading and error states say nothing
  // about a count they don't have yet, rather than claiming a number of zero.
  const criteriaCountLabel =
    !isLoading && !error
      ? `All ${criteria.length} Table 6 criteria used by the mapper. Select View for the full standard text.`
      : 'Table 6 criteria used by the mapper. Select View for the full standard text.';

  return (
    <div className="min-h-screen bg-[#F8F8FF]">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">GRIHA Criteria</h1>
          <p className="text-gray-600">{criteriaCountLabel}</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : error ? (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Failed to load criteria')}</p>
          </div>
        ) : criteria.length === 0 ? (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
            <div className="text-center py-8">
              <BookOpenIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
              <h3 className="text-sm font-medium text-gray-900">No criteria loaded</h3>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#F8F8FF]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criterion</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max points</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detail</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {criteria.map((criterion) => (
                  <tr key={criterion.id} className="hover:bg-[#F8F8FF]">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {criterion.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {criterion.section}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{criterion.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {criterion.max_points}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {criterion.appraisal_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {criterion.has_detail ? 'Full text' : 'Summary only'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        type="button"
                        onClick={() => setSelectedId(criterion.id)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail panel */}
        {selectedId && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-900">{selectedId}</h3>
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {detailLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : detailError ? (
                  <p className="text-sm text-red-600">{getApiErrorMessage(detailError, 'Failed to load criterion')}</p>
                ) : detail ? (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">{detail.title}</h4>
                      <p className="text-sm text-gray-500">{detail.section}</p>
                    </div>
                    {detail.intent && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 mb-1">Intent</h5>
                        <p className="text-sm text-gray-700 whitespace-pre-line">{detail.intent}</p>
                      </div>
                    )}
                    {detail.standard_body_markdown && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 mb-1">Standard text</h5>
                        <pre className="bg-[#F8F8FF] rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap font-sans">
                          {detail.standard_body_markdown}
                        </pre>
                      </div>
                    )}
                    {Array.isArray(detail.observable_items) && detail.observable_items.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 mb-1.5">Observable check-items</h5>
                        <div className="flex flex-wrap gap-1.5">
                          {detail.observable_items.map((item, idx) => {
                            const text = typeof item === 'string' ? item : item?.text;
                            if (!text) return null;
                            const key = (typeof item === 'object' && item?.id) || `${selectedId}-obs-${idx}`;
                            return (
                              <span
                                key={key}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-900"
                              >
                                {text}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {Array.isArray(detail.compliance) && detail.compliance.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 mb-1">Compliance evidence</h5>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                          {detail.compliance.map((line, idx) => (
                            <li key={idx}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
